import React, { useState, useEffect } from 'react';
import { updateAdminConfig, buyToken, testBumpPrice, testBackdateBuyPeriod, testResetUser, testResetAll } from '../../utils/api';

/*
  Test Console — exercise the whole platform without real crypto.
  Turn ON Test Mode, then:
    1. Simulate a buy for any registered wallet (no MetaMask, no on-chain).
    2. Bump the token price to grow portfolios and test the 2× sell unlock.
    3. Backdate a user's buy period to test the rolling-cap reset.
    4. Then open that user's dashboard to test sell / claim / etc.
*/

const TestConsole = ({ config, loadConfig, flash, SYM = 'ROBO', BC = 'USDT' }) => {
  const [testMode, setTestMode] = useState(!!config.testMode);
  const [savingMode, setSavingMode] = useState(false);
  const [testMonth, setTestMonth] = useState(config.testMonthOverride || '');
  const [savingMonth, setSavingMonth] = useState(false);

  // simulate buy
  const [buyWallet, setBuyWallet] = useState('');
  const [buyAmount, setBuyAmount] = useState('100');
  const [buyCurrency, setBuyCurrency] = useState(config.baseCurrency || 'USDT');
  const [buying, setBuying] = useState(false);

  // bump price
  const [mult, setMult] = useState('2');
  const [absPrice, setAbsPrice] = useState('');
  const [bumping, setBumping] = useState(false);

  // backdate
  const [bdRobo, setBdRobo] = useState('');
  const [bdDays, setBdDays] = useState('181');
  const [bdDate, setBdDate] = useState('');
  const [bding, setBding] = useState(false);

  // reset / revert
  const [resetRobo, setResetRobo] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resettingAll, setResettingAll] = useState(false);

  // exempt
  const [exWallet, setExWallet] = useState('');
  const [exSaving, setExSaving] = useState(false);

  useEffect(() => { setTestMode(!!config.testMode); setTestMonth(config.testMonthOverride || ''); }, [config]);

  const saveMonth = async (val) => {
    setSavingMonth(true);
    try { await updateAdminConfig({ testMonthOverride: val || null }); flash(val ? `Test month set to ${val}` : 'Test month cleared'); loadConfig(); }
    catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setSavingMonth(false);
  };

  const currencies = (config.paymentCurrencies || []).filter(c => c.isActive);
  const exemptList = config.sellExemptWallets || [];

  const toggleMode = async () => {
    setSavingMode(true);
    try {
      const next = !testMode;
      await updateAdminConfig({ testMode: next });
      setTestMode(next);
      flash(next ? 'Test Mode ON — buys skip crypto/on-chain' : 'Test Mode OFF');
      loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setSavingMode(false);
  };

  const doBuy = async () => {
    if (!buyWallet || buyWallet.length < 42) { flash('Enter a valid wallet address', 'error'); return; }
    if (!(parseFloat(buyAmount) > 0)) { flash('Enter a valid amount', 'error'); return; }
    setBuying(true);
    try {
      const r = await buyToken({ walletAddress: buyWallet.trim(), amount: parseFloat(buyAmount), currency: buyCurrency, txHash: null });
      flash(`Simulated buy: +${(r.transaction?.tokensReceived || 0).toFixed(2)} ${SYM} to ${buyWallet.slice(0, 8)}…`);
      loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Buy failed', 'error'); }
    setBuying(false);
  };

  const doBump = async () => {
    setBumping(true);
    try {
      const body = absPrice ? { price: parseFloat(absPrice) } : { multiplier: parseFloat(mult) || 2 };
      const r = await testBumpPrice(body);
      flash(r.message || 'Price updated'); loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setBumping(false);
  };

  const doBackdate = async () => {
    if (!bdRobo) { flash('Enter a ROBO ID', 'error'); return; }
    setBding(true);
    try {
      const body = bdDate ? { roboId: bdRobo.trim(), date: bdDate } : { roboId: bdRobo.trim(), daysAgo: parseInt(bdDays) || 0 };
      const r = await testBackdateBuyPeriod(body);
      flash(r.message || 'Backdated'); loadConfig();
    } catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setBding(false);
  };

  const doResetUser = async () => {
    if (!resetRobo) { flash('Enter a ROBO ID', 'error'); return; }
    if (!window.confirm(`Reset ${resetRobo}? Balances zeroed and history deleted (user kept).`)) return;
    setResetting(true);
    try { const r = await testResetUser({ roboId: resetRobo.trim() }); flash(r.message || 'Reset'); loadConfig(); }
    catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setResetting(false);
  };

  const doResetAll = async () => {
    const phrase = window.prompt('⚠ This clears EVERY user\'s balances, price, supply and ALL history — REAL and test data alike. This cannot be undone without a database backup.\n\nType RESET-ALL to confirm:');
    if (phrase !== 'RESET-ALL') { flash('Cancelled — confirmation did not match', 'error'); return; }
    setResettingAll(true);
    try { const r = await testResetAll({ confirm: 'RESET-ALL' }); flash(r.message || 'Reset all'); loadConfig(); }
    catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setResettingAll(false);
  };

  const saveExempt = async (list) => {
    setExSaving(true);
    try { await updateAdminConfig({ sellExemptWallets: list }); flash(`Exempt wallets saved (${list.length})`); loadConfig(); }
    catch (e) { flash(e.response?.data?.error || 'Failed', 'error'); }
    setExSaving(false);
  };
  const addExempt = () => {
    const w = (exWallet || '').trim().toLowerCase();
    if (!w || w.length < 42) { flash('Enter a valid wallet address', 'error'); return; }
    if (exemptList.map(x => x.toLowerCase()).includes(w)) { flash('Already exempt', 'error'); return; }
    saveExempt([...exemptList, w]); setExWallet('');
  };
  const removeExempt = (w) => saveExempt(exemptList.filter(x => x.toLowerCase() !== w.toLowerCase()));

  return (
    <div className="fade-in">
      <style>{`
        .tc-card { padding: 20px 22px; border-radius: 14px; margin-bottom: 18px; }
        .tc-card h3 { margin: 0 0 4px; font-size: 15px; }
        .tc-desc { margin: 0 0 14px; font-size: 12.5px; opacity: .72; line-height: 1.55; }
        .tc-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 14px; }
        .tc-field label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 6px; }
        .tc-field .input { width: 100%; }
        .tc-mt { margin-top: 14px; }
        .tc-mode { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; }
        .tc-toggle { position: relative; width: 52px; height: 30px; border-radius: 999px; background: rgba(128,128,128,.35); transition: background .2s; flex-shrink: 0; cursor: pointer; border: none; }
        .tc-toggle.on { background: #16a34a; }
        .tc-knob { position: absolute; top: 3px; left: 3px; width: 24px; height: 24px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.35); transition: transform .2s; }
        .tc-toggle.on .tc-knob { transform: translateX(22px); }
        .tc-banner { padding: 10px 14px; border-radius: 10px; font-size: 13px; font-weight: 600; margin-bottom: 16px; }
        .tc-banner.on { background: rgba(22,163,74,.14); color: #16a34a; border: 1px solid rgba(22,163,74,.3); }
        .tc-banner.off { background: rgba(148,163,184,.14); color: var(--text-2); border: 1px solid var(--border); }
        .tc-steps { font-size: 12.5px; line-height: 1.7; opacity: .85; }
        .tc-steps b { color: var(--accent); }
      `}</style>

      <h2 className="adm-title">Test Console</h2>

      <div className={`tc-banner ${testMode ? 'on' : 'off'}`}>
        {testMode
          ? '● TEST MODE IS ON — buys need no crypto, no MetaMask, no on-chain verification.'
          : '○ Test Mode is OFF — buys require a real on-chain payment. Turn it ON to test freely.'}
      </div>

      {/* Test Mode toggle */}
      <div className="card tc-card">
        <div className="tc-mode">
          <div>
            <h3>Test Mode</h3>
            <p className="tc-desc" style={{ margin: 0 }}>When ON, a buy is credited instantly (tokens, level income, splits accounting) without any real payment.</p>
          </div>
          <button type="button" role="switch" aria-checked={testMode} className={`tc-toggle ${testMode ? 'on' : ''}`} disabled={savingMode} onClick={toggleMode}>
            <span className="tc-knob" />
          </button>
        </div>
      </div>

      {/* Test Month (royalty monthly testing) */}
      <div className="card tc-card">
        <h3>Test Month <span style={{ fontWeight: 400, opacity: .6, fontSize: 12 }}>— royalty / monthly eligibility</span></h3>
        <p className="tc-desc">Force royalty to compute for a chosen month. While set, <strong>test buys are dated inside this month</strong>, so you can build a team's monthly volume and test eligibility + the month reset for any period. Currently: <strong>{config.testMonthOverride || 'real current month'}</strong>.</p>
        <div className="tc-form">
          <div className="tc-field">
            <label>Month</label>
            <input type="month" className="input" value={testMonth} onChange={e => setTestMonth(e.target.value)} />
          </div>
        </div>
        <div className="tc-mt" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => saveMonth(testMonth)} disabled={savingMonth || !testMonth}>Set Test Month</button>
          <button className="btn btn-outline" onClick={() => { setTestMonth(''); saveMonth(''); }} disabled={savingMonth || !config.testMonthOverride}>Clear (use real month)</button>
        </div>
      </div>

      {/* 1. Simulate buy */}
      <div className="card tc-card">
        <h3>1 · Simulate a Buy</h3>
        <p className="tc-desc">Credit a buy to any registered wallet — no wallet popup. Requires Test Mode ON.</p>
        <div className="tc-form">
          <div className="tc-field" style={{ gridColumn: '1 / -1' }}>
            <label>Wallet address (registered user)</label>
            <input className="input" value={buyWallet} onChange={e => setBuyWallet(e.target.value)} placeholder="0x..." style={{ fontFamily: 'var(--font-mono)' }} />
          </div>
          <div className="tc-field">
            <label>Amount</label>
            <input type="number" className="input" value={buyAmount} onChange={e => setBuyAmount(e.target.value)} />
          </div>
          <div className="tc-field">
            <label>Currency</label>
            <select className="input" value={buyCurrency} onChange={e => setBuyCurrency(e.target.value)}>
              {currencies.length ? currencies.map(c => <option key={c.symbol} value={c.symbol}>{c.symbol}</option>) : <option value="USDT">USDT</option>}
            </select>
          </div>
        </div>
        <button className="btn btn-primary tc-mt" onClick={doBuy} disabled={buying || !testMode}>{buying ? 'Buying…' : 'Simulate Buy'}</button>
      </div>

      {/* 2. Bump price */}
      <div className="card tc-card">
        <h3>2 · Grow Price (test the 2× sell unlock)</h3>
        <p className="tc-desc">Raise the token price so portfolios climb toward 2× invested and the sell gate unlocks. Current price: <strong>{Number(config.tokenPriceInUsdt || 0).toFixed(6)} USDT</strong>.</p>
        <div className="tc-form">
          <div className="tc-field">
            <label>Multiply price by</label>
            <input type="number" className="input" value={mult} onChange={e => { setMult(e.target.value); setAbsPrice(''); }} placeholder="e.g. 2" />
          </div>
          <div className="tc-field">
            <label>…or set exact price</label>
            <input type="number" className="input" value={absPrice} onChange={e => setAbsPrice(e.target.value)} placeholder="e.g. 0.05" />
          </div>
        </div>
        <button className="btn btn-primary tc-mt" onClick={doBump} disabled={bumping || !testMode}>{bumping ? 'Updating…' : 'Apply Price'}</button>
      </div>

      {/* 3. Backdate buy period */}
      <div className="card tc-card">
        <h3>3 · Backdate Buy Period (test the cap reset)</h3>
        <p className="tc-desc">Move a user's buy history into the past — by "days ago" or an exact date. If it's older than the {config.buyCapPeriodMonths || 6}-month window, their next buy resets the allowance.</p>
        <div className="tc-form">
          <div className="tc-field">
            <label>User ROBO ID</label>
            <input className="input" value={bdRobo} onChange={e => setBdRobo(e.target.value)} placeholder="ROBO000002" />
          </div>
          <div className="tc-field">
            <label>Days ago</label>
            <input type="number" className="input" value={bdDays} onChange={e => { setBdDays(e.target.value); setBdDate(''); }} disabled={!!bdDate} />
          </div>
          <div className="tc-field">
            <label>…or exact date</label>
            <input type="date" className="input" value={bdDate} onChange={e => setBdDate(e.target.value)} />
          </div>
        </div>
        <button className="btn btn-primary tc-mt" onClick={doBackdate} disabled={bding || !testMode}>{bding ? 'Applying…' : (bdDate ? 'Set Date' : 'Backdate Period')}</button>
      </div>

      {/* 4. Exempt wallets */}
      <div className="card tc-card">
        <h3>4 · Sell-Exempt Wallets (test no-cap withdrawal)</h3>
        <p className="tc-desc">Exempt wallets ignore the 2× gate/cap — they can withdraw any amount, anytime. Add a test wallet, then sell from its dashboard to confirm.</p>
        <div className="tc-form">
          <div className="tc-field" style={{ gridColumn: '1 / -1' }}>
            <label>Wallet address</label>
            <input className="input" value={exWallet} onChange={e => setExWallet(e.target.value)} placeholder="0x..." style={{ fontFamily: 'var(--font-mono)' }} />
          </div>
        </div>
        <button className="btn btn-primary tc-mt" onClick={addExempt} disabled={exSaving}>Add to Exempt</button>
        {exemptList.length > 0 && (
          <div style={{ marginTop: 14 }}>
            {exemptList.map(w => (
              <div key={w} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px dashed var(--border)', fontSize: 12.5 }}>
                <span className="mono" style={{ wordBreak: 'break-all' }}>{w}</span>
                <button className="btn btn-sm btn-outline" onClick={() => removeExempt(w)} disabled={exSaving}>Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reset / revert */}
      <div className="card tc-card" style={{ borderColor: 'rgba(239,68,68,.4)' }}>
        <h3>Reset / Revert Test Data</h3>
        <p className="tc-desc" style={{ color: '#ef4444', fontWeight: 600 }}>
          ⚠ Warning: this database holds BOTH test and real data. "Reset ALL" wipes EVERY user's balances,
          invested amounts, price, supply and history — real investors included. It cannot be undone without a
          DB backup. For safe testing, use a separate test database (see notes below).
        </p>
        <div className="tc-form">
          <div className="tc-field">
            <label>Reset one user (ROBO ID)</label>
            <input className="input" value={resetRobo} onChange={e => setResetRobo(e.target.value)} placeholder="ROBO000002" />
          </div>
          <div className="tc-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn btn-danger" style={{ width: '100%' }} onClick={doResetUser} disabled={resetting}>{resetting ? 'Resetting…' : 'Reset This User'}</button>
          </div>
        </div>
        <div className="tc-mt" style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <button className="btn btn-danger" onClick={doResetAll} disabled={resettingAll}>{resettingAll ? 'Resetting…' : 'Reset ALL Test Data'}</button>
          <p className="tc-desc" style={{ marginTop: 8, marginBottom: 0 }}>Clears every balance, price, supply, pool and all history in one go — clean slate for re-testing.</p>
        </div>
      </div>

      {/* How to test */}
      <div className="card tc-card">
        <h3>How to test everything</h3>
        <div className="tc-steps">
          <b>Buy cap:</b> Simulate buys for a wallet (step 1) until the total reaches the cap → next buy is rejected. Backdate the period (step 3) → buying resets.<br />
          <b>Level income:</b> Buy from a wallet whose upline has invested → check the upline's Level Income (Users → View, or their dashboard).<br />
          <b>Sell unlock (2×):</b> After a buy, use step 2 to grow the price until portfolio ≥ 2× invested → the user's dashboard shows "Withdrawable" unlocked → submit a sell → approve it in Sell Withdrawals.<br />
          <b>Claim / Royalty:</b> Once level income / royalty accrues, claim from the dashboard → approve in Claims / Royalty.<br />
          <b>Pauses:</b> Toggle Buy/Sell/Claim/P2P in Controls → confirm the user panel disables them.<br />
          <b>Exempt wallet:</b> Add a wallet in Controls → Sell-Exempt → it can withdraw with no 2× gate.
        </div>
      </div>
    </div>
  );
};

export default TestConsole;
